/**
 * Tariff Transaction Detail Modal
 * Shows detailed input and output data for a tariff classification transaction
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getTransactionDetails, downloadResults, triggerDownload } from '../../../services/tariffClassifierPageService';
import { useTranslation } from '../../../store/languageStore';

interface TariffTransactionDetailModalProps {
  open: boolean;
  onClose: () => void;
  transactionId: string | null;
}

const TariffTransactionDetailModal: React.FC<TariffTransactionDetailModalProps> = ({
  open,
  onClose,
  transactionId,
}) => {
  const { t } = useTranslation();
  const [transaction, setTransaction] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && transactionId) {
      loadTransactionDetails();
    }
  }, [open, transactionId]);

  const loadTransactionDetails = async () => {
    if (!transactionId) return;

    try {
      setLoading(true);
      const data = await getTransactionDetails(transactionId);
      setTransaction(data);
    } catch (error) {
      console.error('Error loading transaction details:', error);
      toast.error(t('dashboard.cards.tariffClassifier.errors.failedToLoadDetails'));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (format: 'csv' | 'excel') => {
    if (!transactionId) return;

    try {
      const blob = await downloadResults(transactionId, format);
      const filename = `tariff_results_${transactionId.substring(0, 8)}.${format === 'excel' ? 'xlsx' : 'csv'}`;
      triggerDownload(blob, filename);
      toast.success(t('dashboard.cards.tariffClassifier.success.downloadStarted'));
    } catch (error) {
      toast.error(t('dashboard.cards.tariffClassifier.errors.downloadFailed'));
    }
  };

  const handleCopyHSCode = (code: string) => {
    if (code) {
      navigator.clipboard.writeText(code);
      toast.success(t('dashboard.cards.tariffClassifier.success.copiedToClipboard'));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight={600}>
          {t('dashboard.cards.tariffClassifier.transactionDetails')}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : transaction ? (
          <Box sx={{ p: 2 }}>
            <Grid container spacing={3} sx={{ minHeight: '500px' }}>
              {/* Input Data Section */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <Typography variant="h6" gutterBottom fontWeight={600} color="primary">
                      📥 {t('dashboard.cards.tariffClassifier.inputData')}
                    </Typography>
                    {transaction.classification_mode === 'direct_input' && transaction.input_data ? (
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>{t('dashboard.cards.tariffClassifier.productDescription')}:</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1.5, p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
                          {transaction.input_data.product_description || t('dashboard.cards.tariffClassifier.noDataAvailable')}
                        </Typography>
                        {transaction.input_data.materials && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            <strong>{t('dashboard.cards.tariffClassifier.materials')}:</strong> {transaction.input_data.materials}
                          </Typography>
                        )}
                        {transaction.input_data.origin_country && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            <strong>{t('dashboard.cards.tariffClassifier.origin')}:</strong> {transaction.input_data.origin_country}
                          </Typography>
                        )}
                        {transaction.input_data.intended_use && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>{t('dashboard.cards.tariffClassifier.intendedUse')}:</strong> {transaction.input_data.intended_use}
                          </Typography>
                        )}
                      </Box>
                    ) : transaction.batch_input_data && transaction.batch_input_data.length > 0 ? (
                      <Box sx={{ width: '100%' }}>
                        {transaction.uploaded_file_name && (
                          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              <strong>{t('dashboard.cards.tariffClassifier.uploadedFile')}:</strong>
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              📄 {transaction.uploaded_file_name}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={async () => {
                                  try {
                                    const { downloadOriginalFile, triggerDownload } = await import('../../../services/tariffClassifierPageService');
                                    const blob = await downloadOriginalFile(transaction.id);
                                    triggerDownload(blob, transaction.uploaded_file_name || 'original.csv');
                                    toast.success(t('dashboard.cards.tariffClassifier.success.fileDownloaded'));
                                  } catch (error) {
                                    toast.error(t('dashboard.cards.tariffClassifier.errors.downloadFailed'));
                                  }
                                }}
                              >
                                {t('dashboard.cards.tariffClassifier.downloadOriginal')}
                              </Button>
                            </Box>
                          </Box>
                        )}
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>{t('dashboard.cards.tariffClassifier.fileContents', { count: transaction.batch_input_data.length })}:</strong>
                        </Typography>
                        <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                          {transaction.batch_input_data.map((item: any, idx: number) => (
                            <Box key={idx} sx={{ mb: 1, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                              <Typography variant="caption" color="primary" fontWeight={600}>
                                {t('dashboard.cards.tariffClassifier.item')} {item.row_number || idx + 1}:
                              </Typography>
                              <Typography variant="body2">
                                {item.original_description || t('dashboard.cards.tariffClassifier.noDataAvailable')}
                              </Typography>
                              {item.original_hs_code && (
                                <Typography variant="caption" color="text.secondary">
                                  {t('dashboard.cards.tariffClassifier.generatedHS')}: {item.original_hs_code}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    ) : transaction.uploaded_file_name ? (
                      <Box sx={{ width: '100%' }}>
                        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            <strong>{t('dashboard.cards.tariffClassifier.uploadedFile')}:</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            📄 {transaction.uploaded_file_name}
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={async () => {
                              try {
                                const { downloadOriginalFile, triggerDownload } = await import('../../../services/tariffClassifierPageService');
                                const blob = await downloadOriginalFile(transaction.id);
                                triggerDownload(blob, transaction.uploaded_file_name || 'original.csv');
                                toast.success(t('dashboard.cards.tariffClassifier.success.fileDownloaded'));
                              } catch (error) {
                                toast.error(t('dashboard.cards.tariffClassifier.errors.downloadFailed'));
                              }
                            }}
                          >
                            {t('dashboard.cards.tariffClassifier.downloadOriginal')}
                          </Button>
                        </Box>
                        <Typography variant="caption" color="text.secondary" fontStyle="italic">
                          {t('dashboard.cards.tariffClassifier.noDataAvailable')}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" fontStyle="italic">
                        {t('dashboard.cards.tariffClassifier.noDataAvailable')}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Output Data Section */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <Typography variant="h6" gutterBottom fontWeight={600} color="success.main">
                      📤 {t('dashboard.cards.tariffClassifier.classificationResults')}
                    </Typography>
                    {transaction.output_data ? (
                      Array.isArray(transaction.output_data) ? (
                        <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, maxHeight:'400px' }}>
                          {transaction.output_data.map((result: any, idx: number) => (
                            <Box key={idx} sx={{ mb: 2, p: 1.5, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                              <Stack spacing={0.5}>
                                <Typography variant="caption" color="primary" fontWeight={600}>
                                  {t('dashboard.cards.tariffClassifier.item')} {result.row_number || idx + 1}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <strong>{t('dashboard.cards.tariffClassifier.generatedHS')}:</strong>{' '}
                                  <Chip label={result.generated_hs_code || t('dashboard.cards.tariffClassifier.noDataAvailable')} size="small" color="primary" />
                                  {result.generated_hs_code && (
                                    <IconButton size="small" onClick={() => handleCopyHSCode(result.generated_hs_code)} title="Copy HS Code">
                                      <CopyIcon fontSize="inherit" sx={{ fontSize: '0.9rem' }} />
                                    </IconButton>
                                  )}
                                </Box>
                                <Typography variant="body2">
                                  <strong>{t('dashboard.cards.tariffClassifier.confidence')}:</strong>{' '}
                                  <Chip
                                    label={`${((result.confidence || 0) * 100).toFixed(0)}%`}
                                    size="small"
                                    color={
                                      (result.confidence || 0) >= 0.9
                                        ? 'success'
                                        : (result.confidence || 0) >= 0.75
                                        ? 'warning'
                                        : 'error'
                                    }
                                  />
                                </Typography>
                              </Stack>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        // Single result for direct classification
                        <Box sx={{ width: '100%' }}>
                          <Stack spacing={1.5}>
                            <Box sx={{ width: '100%' }}>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                <strong>{t('dashboard.cards.tariffClassifier.generatedHSCode')}:</strong>
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                  label={transaction.output_data.generated_hs_code || t('dashboard.cards.tariffClassifier.noDataAvailable')}
                                  size="medium"
                                  color="primary"
                                  sx={{ fontWeight: 600, fontSize: '1rem' }}
                                />
                                {transaction.output_data.generated_hs_code && (
                                  <IconButton size="small" onClick={() => handleCopyHSCode(transaction.output_data.generated_hs_code)} title="Copy HS Code">
                                    <CopyIcon fontSize="inherit" sx={{ fontSize: '1.2rem' }} />
                                  </IconButton>
                                )}
                              </Box>
                            </Box>
                            <Box sx={{ width: '100%' }}>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                <strong>{t('dashboard.cards.tariffClassifier.confidence')}:</strong>
                              </Typography>
                              <Chip
                                label={`${((transaction.output_data.confidence || 0) * 100).toFixed(0)}%`}
                                size="medium"
                                color={
                                  (transaction.output_data.confidence || 0) >= 0.9
                                    ? 'success'
                                    : (transaction.output_data.confidence || 0) >= 0.75
                                    ? 'warning'
                                    : 'error'
                                }
                              />
                            </Box>
                            {transaction.output_data.reasoning && (
                              <Box sx={{ width: '100%' }}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  <strong>{t('dashboard.cards.tariffClassifier.reasoning')}:</strong>
                                </Typography>
                                <Typography variant="body2" sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
                                  {transaction.output_data.reasoning}
                                </Typography>
                              </Box>
                            )}
                          </Stack>
                        </Box>
                      )
                    ) : (
                      <Typography variant="body2" color="text.secondary" fontStyle="italic">
                        {t('dashboard.cards.tariffClassifier.noResultsAvailable')}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Processing Info */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                      ⚙️ {t('dashboard.cards.tariffClassifier.processingInformation')}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">
                          {t('dashboard.cards.tariffClassifier.processingTime')}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {transaction.processing_time_ms ? `${transaction.processing_time_ms}ms` : t('dashboard.cards.tariffClassifier.noDataAvailable')}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">
                          {t('dashboard.cards.tariffClassifier.status')}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          <Chip
                            label={transaction.status}
                            size="small"
                            color={
                              transaction.status === 'completed'
                                ? 'success'
                                : transaction.status === 'processing'
                                ? 'primary'
                                : transaction.status === 'pending'
                                ? 'warning'
                                : 'error'
                            }
                          />
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">
                          {t('dashboard.cards.tariffClassifier.createdAt')}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {new Date(transaction.created_at).toLocaleString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">
                          {t('dashboard.cards.tariffClassifier.transactionId')}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.75rem' }}>
                          {transaction.id}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              {t('dashboard.cards.tariffClassifier.noDataAvailable')}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {transaction && transaction.status === 'completed' && (
          <>
            <Button
              startIcon={<DownloadIcon />}
              onClick={() => handleDownload('csv')}
              variant="outlined"
            >
              {t('dashboard.cards.tariffClassifier.downloadCSV')}
            </Button>
            <Button
              startIcon={<DownloadIcon />}
              onClick={() => handleDownload('excel')}
              variant="outlined"
            >
              {t('dashboard.cards.tariffClassifier.downloadExcel')}
            </Button>
          </>
        )}
        <Button onClick={onClose} variant="contained">
          {t('dashboard.cards.tariffClassifier.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TariffTransactionDetailModal;
